import { axiosInstance } from '@/Api/axiosV2Config';
import type {
  Part,
  PartListItem,
  PartListResponse,
  PartCategory,
  PartTreeResponse,
  PartFilters,
} from '../types/part.types';

/**
 * API client pour la gestion des pièces automobiles
 */
export const partApi = {
  // ==================== PIÈCES ====================

  /**
   * Lister les pièces avec filtres
   */
  getParts: async (filters?: PartFilters): Promise<PartListResponse> => {
    const { data } = await axiosInstance.get<{ data: PartListItem[]; total: number }>('/parts/v2', {
      params: {
        skip: 0,
        take: 10000, // Récupérer toutes les pièces
        locale: filters?.locale || 'fr',
      },
    });
    return {
      parts: data.data || [],
      total: data.total || 0,
    };
  },

  /**
   * Récupérer une pièce par ID
   */
  getPart: async (id: number): Promise<Part> => {
    const { data } = await axiosInstance.get<Part>(`/parts/${id}`);
    return data;
  },

  /**
   * Récupérer l'arbre hiérarchique des pièces
   * Retourne les catégories, sous-catégories et pièces organisées en arbre
   */
  getPartsTree: async (locale = 'fr', vehicleType?: number): Promise<PartTreeResponse> => {
    const { data } = await axiosInstance.get<PartTreeResponse>('/parts/v2/tree', {
      params: { locale, vehicleType },
    });
    return data;
  },

  /**
   * Rechercher des pièces par texte
   */
  searchParts: async (query: string, locale = 'fr'): Promise<PartListItem[]> => {
    const { data } = await axiosInstance.get<{ data: PartListItem[] }>('/parts/v2/search', {
      params: { q: query, locale },
    });
    return data.data;
  },

  // ==================== CATÉGORIES ====================

  /**
   * Lister toutes les catégories
   */
  getCategories: async (locale = 'fr'): Promise<PartCategory[]> => {
    const { data } = await axiosInstance.get<{ data: PartCategory[] }>('/parts/v2/categories', {
      params: { locale },
    });
    return data.data;
  },

  /**
   * Récupérer une catégorie par ID
   */
  getCategory: async (id: number, locale = 'fr'): Promise<PartCategory> => {
    const { data } = await axiosInstance.get<PartCategory>(`/parts/categories/${id}`, {
      params: { locale },
    });
    return data;
  },

  /**
   * Récupérer les catégories racines (top-level)
   */
  getRootCategories: async (locale = 'fr'): Promise<PartCategory[]> => {
    const { data } = await axiosInstance.get<PartCategory[]>('/parts/categories/roots', {
      params: { locale },
    });
    return data;
  },

  /**
   * Récupérer les sous-catégories d'une catégorie
   */
  getSubcategories: async (categoryId: number, locale = 'fr'): Promise<PartCategory[]> => {
    const { data } = await axiosInstance.get<PartCategory[]>(
      `/parts/categories/${categoryId}/subcategories`,
      {
        params: { locale },
      },
    );
    return data;
  },

  /**
   * Récupérer les pièces d'une catégorie
   */
  getPartsByCategory: async (categoryId: number, locale = 'fr'): Promise<PartListItem[]> => {
    const { data } = await axiosInstance.get<PartListItem[]>(
      `/parts/categories/${categoryId}/parts`,
      {
        params: { locale },
      },
    );
    return data;
  },

  // ==================== SÉLECTION POUR GARANTIES ====================

  /**
   * Récupérer les pièces recommandées pour un type de garantie
   */
  getRecommendedParts: async (
    warrantyType: number,
    vehicleType: number,
    locale = 'fr',
  ): Promise<PartListItem[]> => {
    const { data } = await axiosInstance.get<{ data: PartListItem[] }>('/parts/v2/recommended', {
      params: { warrantyType, vehicleType, locale },
    });
    return data.data;
  },

  /**
   * Récupérer les pièces essentielles (priorité 1)
   */
  getEssentialParts: async (locale = 'fr'): Promise<PartListItem[]> => {
    const { data } = await axiosInstance.get<{ data: PartListItem[] }>('/parts/v2/essential', {
      params: { locale },
    });
    return data.data;
  },
};
